import { format } from 'echarts/core';

import { FONT_SIZE } from './shared';

// A label's line: out from the pie's edge, then along to the label (ECharts'
// defaults)
export const PIE_LABEL_LINE_LENGTH = 15;
export const PIE_LABEL_LINE_LENGTH2 = 15;
// Room kept between a label and the chart's edge (ECharts' bleedMargin)
const BLEED_MARGIN = 10;
// The smallest a pie shrinks to for its labels (Highcharts' minSize of 80)
const MIN_RADIUS = 40;
// Room above and below a label in its column
const LABEL_PADDING = 2;
// The pie is shrunk again at most this often as its labels are re-measured
const MAX_FITS = 3;

interface Box {
  index: number;
  // Where it would sit, and its height, down the column
  target: number;
  size: number;
  // Labels of bigger parts are kept first
  rank: number;
}

// Places labels of differing heights down a column of the given length, each
// as near its target as it can be, as Highcharts' `distribute` does: the
// lowest ranked are left out while they don't all fit, or while one would be
// further than maxDistance from its target. Returns the boxes placed.
export const distribute = ({
  boxes,
  len,
  maxDistance,
}: {
  boxes: Box[];
  len: number;
  maxDistance: number;
}): Box[] => {
  const byRank = [...boxes].sort((a, b) => b.rank - a.rank);
  for (let room = len; room > len * 0.1; room -= len * 0.1) {
    const kept = [...byRank];
    let total = kept.reduce((sum, box) => sum + box.size, 0);
    while (kept.length && total > room) {
      total -= (kept.pop() as Box).size;
    }
    kept.sort((a, b) => a.target - b.target);

    // Overlapping boxes are joined, centred on their targets, until none
    // overlap
    const groups = kept.map((box) => ({
      size: box.size,
      targets: [box.target],
      pos: 0,
    }));
    let isOverlapping = true;
    while (isOverlapping) {
      groups.forEach((group) => {
        const target =
          (Math.min(...group.targets) + Math.max(...group.targets)) / 2;
        group.pos = Math.min(
          Math.max(target - group.size / 2, 0),
          len - group.size,
        );
      });
      isOverlapping = false;
      for (let i = groups.length - 1; i > 0; i--) {
        const before = groups[i - 1];
        if (before.pos + before.size > groups[i].pos) {
          before.size += groups[i].size;
          before.targets = before.targets.concat(groups[i].targets);
          if (before.pos + before.size > len) {
            before.pos = len - before.size;
          }
          groups.splice(i, 1);
          isOverlapping = true;
        }
      }
    }

    let index = 0;
    const isTooFar = groups.some((group) => {
      let offset = 0;
      return group.targets.some(() => {
        const box = kept[index];
        if (Math.abs(group.pos + offset - box.target) > maxDistance) {
          return true;
        }
        offset += box.size;
        index++;
        return false;
      });
    });
    if (!isTooFar) {
      return kept;
    }
  }
  return [];
};

// A pie's radius and centre, and which of its parts are labelled, as the
// Highcharts pie lays them out: labels stack in a column either side, kept
// near their slices (the smallest parts' left out where they can't be), and
// the pie shrinks, and moves aside, so the widest of them fit beside it.
export const layoutPieLabels = ({
  parts,
  size: { width, height },
  fontFamily,
}: {
  parts: { name: string; value: number }[];
  size: { width: number; height: number };
  fontFamily: string;
}): { radius: number; centerX: number; labelledIndexes: Set<number> } => {
  const font = `${FONT_SIZE}px ${fontFamily}`;
  const total = parts.reduce((sum, part) => sum + Math.max(part.value, 0), 0);
  // Clockwise from 12 o'clock, as ECharts draws it
  let sweep = 0;
  const labels = parts.map((part, index) => {
    const share = total > 0 ? Math.max(part.value, 0) / total : 0;
    const midAngle = (sweep + share / 2) * 2 * Math.PI;
    sweep += share;
    const rect = format.getTextRect(part.name, font);
    return {
      index,
      rank: part.value,
      isRight: Math.sin(midAngle) >= 0,
      dy: -Math.cos(midAngle),
      width: rect.width,
      lineHeight: rect.height,
    };
  });
  const lineHeight = Math.max(0, ...labels.map((label) => label.lineHeight));
  const maxRadius = Math.max(
    MIN_RADIUS,
    Math.min(width, height) / 2 - PIE_LABEL_LINE_LENGTH - lineHeight,
  );
  // The room for labels on one side of the pie
  const toRoom = ({
    radius,
    centerX,
    isRight,
  }: {
    radius: number;
    centerX: number;
    isRight: boolean;
  }) =>
    (isRight ? width - centerX : centerX) -
    radius -
    PIE_LABEL_LINE_LENGTH -
    PIE_LABEL_LINE_LENGTH2 -
    BLEED_MARGIN;

  // The labels kept in each half's column, for a pie of the given radius and
  // centre. Labels wider than the room beside the pie wrap, so stand taller.
  const layoutAt = ({
    radius,
    centerX,
  }: {
    radius: number;
    centerX: number;
  }) => {
    const top = height / 2 - radius - PIE_LABEL_LINE_LENGTH;
    const len = 2 * (radius + PIE_LABEL_LINE_LENGTH) + lineHeight;
    return [true, false].flatMap((isRight) => {
      const room = toRoom({ radius, centerX, isRight });
      return distribute({
        boxes: labels
          .filter((label) => label.isRight === isRight && label.rank > 0)
          .map((label) => {
            const lines =
              room > 0 ? Math.max(1, Math.ceil(label.width / room)) : 1;
            const size = lines * label.lineHeight + 2 * LABEL_PADDING;
            const naturalY =
              height / 2 + label.dy * (radius + PIE_LABEL_LINE_LENGTH);
            return {
              index: label.index,
              target: naturalY - top + size / 2,
              size,
              rank: label.rank,
            };
          }),
        len,
        maxDistance: len / 5,
      });
    });
  };

  // How far the widest kept label on one side overruns the room beside the
  // pie
  const toOverrun = ({
    kept,
    radius,
    centerX,
    isRight,
  }: {
    kept: Box[];
    radius: number;
    centerX: number;
    isRight: boolean;
  }) =>
    Math.max(
      0,
      ...kept
        .filter((box) => labels[box.index].isRight === isRight)
        .map(
          (box) =>
            labels[box.index].width - toRoom({ radius, centerX, isRight }),
        ),
    );

  // Shrunk by as much as the labels overrun either side, and moved away from
  // the side that overruns more
  let radius = maxRadius;
  let centerX = width / 2;
  let kept = layoutAt({ radius, centerX });
  for (let i = 0; i < MAX_FITS; i++) {
    const left = toOverrun({ kept, radius, centerX, isRight: false });
    const right = toOverrun({ kept, radius, centerX, isRight: true });
    const fitted = Math.max(MIN_RADIUS, radius - (left + right) / 2);
    const fittedX = Math.min(
      Math.max(centerX + (left - right) / 2, fitted),
      width - fitted,
    );
    if (fitted >= radius && Math.abs(fittedX - centerX) < 1) {
      break;
    }
    radius = fitted;
    centerX = fittedX;
    kept = layoutAt({ radius, centerX });
  }
  return {
    radius,
    centerX,
    labelledIndexes: new Set(kept.map((box) => box.index)),
  };
};
